import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { fmtError } from "@/lib/api";
import { ACTIVITY_PURPOSES } from "@/lib/activityPurposes";

export default function NewFollowupDialog({ open, onOpenChange, onCreated, defaultDealId, defaultClientId }) {
  const [title, setTitle] = useState("");
  const [purpose, setPurpose] = useState("follow_up");
  const [dueDate, setDueDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  });
  const [dealId, setDealId] = useState(defaultDealId || "");
  const [clientId, setClientId] = useState(defaultClientId || "");
  const [notes, setNotes] = useState("");
  const [deals, setDeals] = useState([]);
  const [clients, setClients] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle(""); setNotes(""); setPurpose("follow_up");
    setDealId(defaultDealId || ""); setClientId(defaultClientId || "");
    api.get("/deals").then((r) => setDeals(r.data)).catch(() => {});
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  }, [open, defaultDealId, defaultClientId]);

  const submit = async () => {
    if (!title || !dueDate) {
      toast.error("Title and date required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title,
        purpose,
        due_date: new Date(dueDate + "T12:00:00").toISOString(),
        deal_id: dealId || null,
        client_id: clientId || null,
        notes,
      };
      await api.post("/followups", payload);
      toast.success("Follow-up scheduled");
      onCreated?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(fmtError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold tracking-tight">New follow-up</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs font-semibold">Action</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Send revised quotation"
              className="rounded-2xl h-11 mt-1.5"
              data-testid="fu-title-input"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold">Purpose</Label>
            <Select value={purpose} onValueChange={setPurpose}>
              <SelectTrigger className="rounded-2xl h-11 mt-1.5" data-testid="fu-purpose-select"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-2xl">
                {ACTIVITY_PURPOSES.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold">Due date</Label>
            <Input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="rounded-2xl h-11 mt-1.5"
              data-testid="fu-date-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold">Client</Label>
              <Select value={clientId} onValueChange={setClientId}>
                <SelectTrigger className="rounded-2xl h-11 mt-1.5"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="rounded-2xl">
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold">Deal</Label>
              <Select value={dealId} onValueChange={setDealId}>
                <SelectTrigger className="rounded-2xl h-11 mt-1.5"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="rounded-2xl">
                  {deals.map((d) => <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold">Notes (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="rounded-2xl mt-1.5"
              rows={2}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-2xl">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-2xl" data-testid="fu-save-button">
            {saving ? "Saving…" : "Schedule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
